import { Button } from '@advanza/shadcn-ui/src/components/ui/button'
import { Card, CardContent } from '@advanza/shadcn-ui/src/components/ui/card'
import { Label } from '@advanza/shadcn-ui/src/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@advanza/shadcn-ui/src/components/ui/select'
import { Divider, LinearLoader } from '@advanza/ui'
import ServicesSelect from 'components/services/ServicesSelect'
import Page from 'components/ui/Page'
import SearchLocationInput from 'components/ui/SearchLocationInput'
import { useCurrentCountry, useUrlQuery } from 'hooks/miscHooks'
import { useCallback, useMemo, useState } from 'react'
import FilterPanel from './FilterPanel'
import ResultsTable from './ResultsTable'
import type { FilterAnswer, ScoringMode, SearchParams, SearchResponse } from './types'
import { useMatchingDebugger } from './useMatchingDebugger'
import { useServiceFilters } from './useServiceFilters'

const MODES = [
    {
        value: 'landingpage',
        label: 'Landing page',
        icon: 'https://static.trustoo.nl/misc/herring-top10.svg',
    },
    {
        value: 'results',
        label: 'Results page',
        icon: 'https://static.trustoo.nl/misc/herring-results.svg',
    },
    { value: 'wizard', label: 'Wizard', icon: 'https://static.trustoo.nl/misc/herring-wizard.svg' },
] as const

const LIMITS = [
    { value: '10', label: '10' },
    { value: '15', label: '15' },
    { value: '20', label: '20' },
    { value: '50', label: '50' },
    { value: '999', label: '999' },
] as const

// Serialize the order config for display: arrays joined by the given separator,
// strings as-is, and anything else (e.g. the raw order object) pretty-printed.
const formatOrder = (order: SearchResponse['order'], separator: string): string => {
    if (order === null || order === undefined) {
        return ''
    }
    if (Array.isArray(order)) {
        return order.join(separator)
    }
    if (typeof order === 'string') {
        return order
    }
    return JSON.stringify(order, null, 2)
}

const MatchingDebuggerPage = () => {
    const { query = {}, onSetQuery } = useUrlQuery() as {
        query: Record<string, any>
        onSetQuery: (diff: Record<string, any>) => void
    }
    const countryCode = useCurrentCountry()
    const [filterAnswers, setFilterAnswers] = useState<FilterAnswer[]>([])
    const [showDebugInfo, setShowDebugInfo] = useState(false)
    const [customOrder, setCustomOrder] = useState<string | null>(null)

    const { filterableQuestions, isLoading: filtersLoading } = useServiceFilters(
        query.service,
        filterAnswers,
    )

    // Only send answers for currently visible questions to the search API
    const visibleQuestionIds = useMemo(
        () => new Set(filterableQuestions.map((q) => q.question_id)),
        [filterableQuestions],
    )
    const activeAnswers = useMemo(
        () => filterAnswers.filter((a) => visibleQuestionIds.has(a.questionId)),
        [filterAnswers, visibleQuestionIds],
    )

    const searchParams = useMemo<Partial<SearchParams>>(
        () => ({
            service: query.service,
            serviceId: query.serviceId ? Number(query.serviceId) : undefined,
            city: query.city,
            _mode: query._mode || 'landingpage',
            limit: Number(query.limit) || 10,
            _order: customOrder || undefined,
            filters: {
                answers: activeAnswers.length > 0 ? activeAnswers : undefined,
            },
        }),
        [
            query.service,
            query.serviceId,
            query.city,
            query._mode,
            query.limit,
            activeAnswers,
            customOrder,
        ],
    )

    const { results, isLoading, timer } = useMatchingDebugger(searchParams)

    const handleServiceChange = useCallback(
        (values: { service_id: number; url: string }[]) => {
            setFilterAnswers([])
            onSetQuery({
                serviceId: values[0].service_id,
                service: values[0].url,
            })
        },
        [onSetQuery],
    )

    const handleApplyCustomOrder = useCallback(() => {
        if (customOrder !== null) {
            onSetQuery({ _order: customOrder })
        }
    }, [customOrder, onSetQuery])

    return (
        <Page title="Ranking Checker">
            {isLoading && <LinearLoader fixed />}
            <Divider l />
            <div className="mx-auto max-w-[1200px] space-y-3 bg-bg min-h-screen px-4 pb-6">
                <h2 className="flex items-center gap-2 text-xl font-semibold">Ranking Checker</h2>

                <Card className="bg-white py-0 gap-0">
                    <CardContent className="space-y-3 p-3">
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="min-w-[180px] flex-1">
                                <Label className="mb-0.5 text-sm text-muted-foreground">
                                    Service
                                </Label>
                                <ServicesSelect
                                    multiple={false}
                                    returnObject
                                    placeholder=""
                                    value={query.serviceId}
                                    onChangeValue={handleServiceChange}
                                />
                            </div>
                            <div className="min-w-[160px] flex-1">
                                <Label className="mb-0.5 text-sm text-muted-foreground">
                                    Location
                                </Label>

                                <SearchLocationInput
                                    value={query.cityName || query.city}
                                    // Matches the border of the shadcn selects beside it
                                    labelClassName="!border-neutral-200"
                                    countryCode={countryCode}
                                    onChangeLocation={(location) =>
                                        onSetQuery({
                                            city: location.url_name ?? '',
                                            cityName: location.place_name,
                                        })
                                    }
                                />
                            </div>
                            <div className="w-[200px]">
                                <Label className="mb-0.5 text-sm text-muted-foreground">Mode</Label>
                                <Select
                                    value={query._mode || 'landingpage'}
                                    onValueChange={(val) => onSetQuery({ _mode: val })}>
                                    <SelectTrigger className="h-8 bg-white text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {MODES.map((m) => (
                                            <SelectItem key={m.value} value={m.value}>
                                                <span className="flex items-center gap-1.5">
                                                    <img
                                                        src={m.icon}
                                                        alt=""
                                                        className="h-12 w-12"
                                                    />
                                                    {m.label}
                                                </span>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="w-[70px]">
                                <Label className="mb-0.5 text-sm text-muted-foreground">
                                    Limit
                                </Label>
                                <Select
                                    value={String(query.limit || 10)}
                                    onValueChange={(val) => onSetQuery({ limit: val })}>
                                    <SelectTrigger className="h-8 bg-white text-sm">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-white">
                                        {LIMITS.map((l) => (
                                            <SelectItem key={l.value} value={l.value}>
                                                {l.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {query.service && filterableQuestions.length > 0 && (
                            <FilterPanel
                                questions={filterableQuestions}
                                selectedAnswers={filterAnswers}
                                onChangeAnswers={setFilterAnswers}
                                isLoading={filtersLoading}
                                disabled={isLoading}
                            />
                        )}
                    </CardContent>
                </Card>

                {/* Results */}
                {results && (
                    <ResultsTable
                        results={results}
                        timer={timer}
                        isLoading={isLoading}
                        mode={(query._mode as ScoringMode) || 'landingpage'}
                        cityUrl={query.city}
                    />
                )}

                {/* Debug info toggle */}
                {results && (
                    <div className="space-y-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowDebugInfo(!showDebugInfo)}>
                            {showDebugInfo ? 'Hide' : 'Show'} debug info
                        </Button>

                        {showDebugInfo && (
                            <Card className="bg-white">
                                <CardContent className="space-y-4 pt-4">
                                    {/* Custom order editor */}
                                    <div className="space-y-2">
                                        <Label className="text-sm text-muted-foreground">
                                            Custom order (Ctrl+Enter to apply)
                                        </Label>
                                        <textarea
                                            className="w-full rounded-md border border-grey-200 p-2 font-mono text-sm"
                                            rows={4}
                                            value={customOrder ?? formatOrder(results.order, ',')}
                                            onChange={(e) => setCustomOrder(e.target.value)}
                                            onKeyDown={(e) => {
                                                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                                                    handleApplyCustomOrder()
                                                }
                                            }}
                                        />
                                    </div>

                                    {/* Order config */}
                                    <div>
                                        <h4 className="text-sm font-medium text-foreground">
                                            Order
                                        </h4>
                                        <pre className="mt-1 max-h-40 overflow-auto rounded bg-grey-200 p-2 text-[11px]">
                                            {formatOrder(results.order, '\n')}
                                        </pre>
                                    </div>

                                    {/* Query stats */}
                                    {results.queries && (
                                        <div>
                                            <h4 className="text-sm font-medium text-foreground">
                                                Queries ({results.queries.length}) &mdash; Total:{' '}
                                                {results.queries.reduce(
                                                    (sum, q) => sum + q.took,
                                                    0,
                                                )}
                                                ms
                                            </h4>
                                            <pre className="mt-1 max-h-60 overflow-auto rounded bg-grey-200 p-2 text-[11px]">
                                                {JSON.stringify(
                                                    results.queries.map((q) => ({
                                                        ...q,
                                                        params: q.params
                                                            ? JSON.stringify(q.params, null, 2)
                                                            : undefined,
                                                    })),
                                                    null,
                                                    2,
                                                )}
                                            </pre>
                                        </div>
                                    )}

                                    {/* Full response */}
                                    <div>
                                        <h4 className="text-sm font-medium text-foreground">
                                            Full response
                                        </h4>
                                        <pre className="mt-1 max-h-60 overflow-auto rounded bg-grey-200 p-2 text-[11px]">
                                            {JSON.stringify(
                                                {
                                                    ...results,
                                                    providers: `[${results.providers?.length} items]`,
                                                    sql: null,
                                                    queries: null,
                                                },
                                                null,
                                                2,
                                            )}
                                        </pre>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                )}
            </div>
            <Divider xxl />
        </Page>
    )
}

export default MatchingDebuggerPage
